import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, Box, TextField, List, ListItem,
  MenuItem, Select, InputLabel, FormControl, Grid, Snackbar, Alert
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
const WorkSessionModalStaff = ({
  open,
  onClose,
  session,
  businessId,
  staffId,
  onUpdate,
  services = [],
  categories = [],
  onShowSuccess,
  fetchMySessions
}) => {
  const [designPrice, setDesignPrice] = useState(0);
  const [servicesSelected, setServicesSelected] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [serviceToAdd, setServiceToAdd] = useState('');
  const [serviceQuantity, setServiceQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const { t } = useTranslation('workSessionModalStaff');

  useEffect(() => {
    setDesignPrice(session?.personalDesignPrice || '');
    setNote(session?.note || '');
    let initialServices = [];
    if (session?.workSessionServices) {
      if (Array.isArray(session.workSessionServices)) {
        initialServices = session.workSessionServices;
      } else if (session.workSessionServices.$values) {
        initialServices = session.workSessionServices.$values;
      }
    }
    setServicesSelected(initialServices);
  }, [session]);

  useEffect(() => {
    setServiceToAdd('');
  }, [selectedCategoryId]);

  const filteredServices = services.filter(
    svc => String(svc.categoryId) === String(selectedCategoryId)
  );

  const handleAddService = () => {
    if (!serviceToAdd) return;
    if (servicesSelected.some(s => s.serviceId === serviceToAdd)) return;
    const svc = services.find(s => s.serviceId === Number(serviceToAdd));
    if (!svc) return;
    setServicesSelected(prev => [
      ...prev,
      {
        serviceId: svc.serviceId,
        serviceName: svc.name,
        quantity: Number(serviceQuantity) || 1,
      }
    ]);
    setSelectedCategoryId('');
    setServiceToAdd('');
    setServiceQuantity(1);
  };

  const handleRemoveService = (id) => {
    setServicesSelected(prev => prev.filter(svc => svc.serviceId !== id));
  };

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      const servicesPayload = servicesSelected.map(svc => ({
        serviceId: svc.serviceId,
        quantity: svc.quantity,
      }));

      const payload = {
        services: servicesPayload,
        designPrice: Number(designPrice) || 0,
        note: note,
        status: "Finished"
      };

      await onUpdate(session.workSessionId, payload, businessId);

      if (typeof fetchMySessions === 'function') {
        await fetchMySessions(staffId);
      }

      if (typeof onShowSuccess === 'function') {
        onShowSuccess("Work session updated successfully!");
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('editSession')}
          <IconButton aria-label={t('close')} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Customer Name */}
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom fontWeight={550}>
              {t('customerName')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={session?.customerName || ""}
              disabled
              sx={{ background: "#f5f5f5" }}
            />
          </Box>

          {/* Note Field */}
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom fontWeight={550}>
              {t('note')}
            </Typography>
            <TextField
              fullWidth
              type="text"
              size="small"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('notePlaceholder')}
              sx={{ background: "#fff" }}
            />
          </Box>

          {/* Select Services */}
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom fontWeight={550}>
              {t('selectServices')}
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="category-label">{t('category')}</InputLabel>
                  <Select
                    labelId="category-label"
                    value={selectedCategoryId}
                    label={t('category')}
                    onChange={e => setSelectedCategoryId(e.target.value)}
                    sx={{ background: "#fff" }}
                  >
                    {categories.map(cat => (
                      <MenuItem key={cat.categoryId} value={cat.categoryId}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            backgroundColor: cat.color,
                            borderRadius: '50%',
                            mr: 1,
                            verticalAlign: 'middle'
                          }}
                        />
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!selectedCategoryId}>
                  <InputLabel id="service-label">{t('service')}</InputLabel>
                  <Select
                    labelId="service-label"
                    value={serviceToAdd}
                    label={t('service')}
                    onChange={e => setServiceToAdd(e.target.value)}
                    sx={{ background: "#fff" }}
                  >
                    {filteredServices.map(svc => (
                      <MenuItem
                        key={svc.serviceId}
                        value={svc.serviceId}
                        disabled={servicesSelected.some(sel => sel.serviceId === svc.serviceId)}
                      >
                        {svc.name} &nbsp; <span style={{ color: '#888', fontSize: '0.9em' }}>€{svc.price}</span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  type="number"
                  size="small"
                  label={t('qty')}
                  value={serviceQuantity}
                  onChange={e => setServiceQuantity(Number(e.target.value))}
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ background: "#fff" }}
                />
              </Grid>
              <Grid item xs={6} sm={1}>
                <IconButton
                  color="primary"
                  onClick={handleAddService}
                  size="medium"
                  disabled={!serviceToAdd}
                  aria-label={t('add')}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>

          {/* Selected Services */}
          <Box mb={3} mt={1}>
            <Typography variant="subtitle2" fontWeight={550}>{t('selectedServices')}</Typography>
            <List dense sx={{ mt: 0.5 }}>
              {(!Array.isArray(servicesSelected) || servicesSelected.length === 0) && (
                <ListItem>
                  <Typography color="textSecondary" fontSize="0.96em">
                    {t('noServices')}
                  </Typography>
                </ListItem>
              )}
              {Array.isArray(servicesSelected) && servicesSelected.map((svc, idx) => (
                <ListItem
                  key={idx}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={t('delete')}
                      onClick={() => handleRemoveService(svc.serviceId)}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Typography variant="body2">
                    {svc.serviceName || t('service')} &nbsp;
                    <span style={{ color: "#888" }}>x{svc.quantity}</span>
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Personal Design Field */}
          <Box mb={1}>
            <Typography variant="subtitle1" gutterBottom fontWeight={550}>
              {t('personalDesign')} <span style={{ fontWeight: 400, color: "#888" }}>(EUR)</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              size="small"
              value={designPrice}
              onChange={e => setDesignPrice(e.target.value)}
              placeholder={t('personalDesignPlaceholder')}
              InputProps={{ inputProps: { min: 0 } }}
              sx={{ background: "#fff" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinish}
            disabled={
              submitting ||
              (
                (servicesSelected.length === 0) &&
                (designPrice === '' || Number(designPrice) === 0)
              )
            }
          >
            {t('finish')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for success */}
      <Snackbar
        open={successOpen}
        autoHideDuration={2000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          {t('successUpdate')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkSessionModalStaff;